import pRetry, { AbortError } from 'p-retry';

import { __BLUEBELL_DOWNLOADER__, __BLUEBELL_GLOBAL_MAP_DOWNLOADER__, BLUEBELL_DOWNLOADER_VERSION } from './constant';

import { createGlobalFactory } from './utils/createGlobalFactory';
import { Debugger } from './utils/Debugger';
import { ensureGlobalMap } from './utils/ensureGlobalMap';

class Downloader extends Debugger {
    static #instance: Downloader;

    readonly #contentMap = ensureGlobalMap<string, string>(__BLUEBELL_GLOBAL_MAP_DOWNLOADER__);

    private constructor() {
        super();
        /* istanbul ignore if: difficult to enter */
        if (Downloader.#instance) {
            throw Error('Downloader cannot be created more than once.');
        }
    }

    protected get debugName(): string {
        return 'downloader';
    }

    public get version(): number {
        return BLUEBELL_DOWNLOADER_VERSION;
    }

    public static getInstance(): Downloader {
        if (!Downloader.#instance) {
            Downloader.#instance = new Downloader();
        }

        return Downloader.#instance;
    }

    public async fetch(src: string, requestOptions?: RequestInit, retries?: number): Promise<Response> {
        const response = await pRetry<Response>(
            async () => {
                const response = await fetch(src, {
                    method: 'GET',
                    cache: 'no-cache',
                    redirect: 'follow',
                    ...requestOptions,
                });

                if (response.status < 200 || response.status >= 300) {
                    throw new AbortError(response.statusText);
                }

                return response;
            },
            {
                retries,
            }
        );

        return response;
    }

    public async download(src: string, requestOptions?: RequestInit, retries?: number): Promise<string> {
        let content = this.#contentMap.get(src);

        if (content) {
            return content;
        }

        content = await this.fetch(src, requestOptions, retries).then(res => res.text());

        if (content) {
            this.#contentMap.set(src, content);
        }

        return content || '';
    }
}

export type { Downloader };

export const getDownloader = createGlobalFactory<Downloader>(
    __BLUEBELL_DOWNLOADER__,
    () => Downloader.getInstance(),
    BLUEBELL_DOWNLOADER_VERSION,
    true // Allow conflict.
);
